/* hp-delivery.js — PDP delivery-time display (HIG-61).
 *
 * Renders the per-variant Lieferzeit from the language-neutral
 * logistics.variants_data metafield (label_key + qty) into [data-hp-delivery],
 * and re-renders on variant change. Same render logic + format as the
 * collection card ("<icon> <qty> Stück <label_lower>", colour per bucket; OOS =
 * bell + "Ausverkauft, Nachschub ist geplant").
 *
 * NOTE: LABEL_CATALOG here is a TEMPORARY copy of the one in
 * assets/hp-collection-card.js — keep both in sync until the planned Shopify
 * `delivery_label` metaobject unifies them (then both read the metaobject).
 */
(function () {
  // HIG-124: the label catalog (48 strings) now lives ONCE in the shared core
  // (window.HPDeliveryCore). The PDP keeps its OWN full-wording renderer below
  // (unit word "Stück", "mehr als 10", OOS text "Ausverkauft, Nachschub ist geplant", 3 lines)
  // — intentionally different from the compact collection cards.
  const UNAVAILABLE_TEXT = { de: 'Ausverkauft, Nachschub ist geplant', en: 'Sold out, restock planned', fr: 'Épuisé, réapprovisionnement prévu', it: 'Esaurito, rifornimento previsto' };
  // Auslaufend (Produkt-Tag «auslaufend», Entscheid Tobias 2026-07-30): kein
  // Nachschub mehr — eigener Text, kein Benachrichtigen-Knopf, Box ohne
  // Portofrei-Zeile und Trennstrich (CSS-Klasse hp-avail--oos-final).
  const UNAVAILABLE_DISCONTINUED = { de: 'Ausverkauft, nicht mehr nachbestellbar', en: 'Sold out, no longer available', fr: 'Épuisé, ne sera plus réapprovisionné', it: 'Esaurito, non più riordinabile' };
  // Kontakt-Zeile bei «Produktion auf Bestellung» (Wortlaut Tobias, 2026-07):
  const CONTACT_STR = {
    de: { pre: 'Fragen zur Lieferzeit?', link: 'Kontaktiere uns' },
    en: { pre: 'Questions about the delivery time?', link: 'Contact us' },
    fr: { pre: 'Des questions sur le délai de livraison?', link: 'Contactez-nous' },
    it: { pre: 'Domande sui tempi di consegna?', link: 'Contattaci' },
  };
  // Unit word per locale with singular/plural (chosen by qty). DE invariant.
  const PIECE_WORD = {
    de: { one: 'Stück', other: 'Stück' },
    en: { one: 'piece', other: 'pieces' },
    fr: { one: 'pièce', other: 'pièces' },
    it: { one: 'pezzo', other: 'pezzi' },
  };
  // Business rule: the frontend never shows a stock number above 10. qty 10
  // renders "10"; qty > 10 renders this localized phrase. Mirrors the same cap
  // in assets/hp-collection-card.js (backend also caps stored qty at 11).
  const QTY_DISPLAY_CAP = 10;
  const MORE_THAN_CAP = { de: 'mehr als 10', en: 'more than 10', fr: 'plus de 10', it: 'più di 10' };
  const ACTIVE_LOCALE = (function () {
    const sl = (window.Shopify && window.Shopify.locale) || 'de';
    const short = String(sl).toLowerCase().split('-')[0];
    return ({ de: 1, en: 1, fr: 1, it: 1 })[short] ? short : 'de';
  })();
  function lookupLabel(k) { return window.HPDeliveryCore.lookupLabel(k); }

  // Delivery ETA text now lives in the shared core (window.HPDeliveryCore.etaText),
  // so the PDP box and the SliderCart compute it the same way (incl. the HPB
  // blocked-days / holiday list). See assets/hp-delivery-core.js.

  // PDP zeigt die 2 schnellsten Stufen (Entscheid Tobias 2026-07-29, vorher 3);
  // die Kategorien-Card zeigt ebenfalls 2. Sorted fastest-first via catalog
  // priority so "fastest N" holds regardless of stored order.
  const PDP_MAX_DELIVERY_LINES = 2;

  function renderBreakdownHTML(stockBreakdown, opts) {
    opts = opts || {};
    // Icon sits in a fixed-width column (.hp-avail__ic) so the text after it lines
    // up across all lines regardless of glyph width (✓ vs ● vs ◐ …).
    if (!stockBreakdown || stockBreakdown.length === 0) {
      const dict = opts.discontinued ? UNAVAILABLE_DISCONTINUED : UNAVAILABLE_TEXT;
      const txt = dict[ACTIVE_LOCALE] || dict.de;
      return '<span class="hp-card__delivery-line hp-card__delivery-line--unavailable"><span class="hp-avail__ic">' + window.HPDeliveryCore.bellSvg(15) + '</span><span>' + txt + '</span></span>';
    }
    const pw = PIECE_WORD[ACTIVE_LOCALE] || PIECE_WORD.de;
    const ordered = stockBreakdown.slice().sort(function (a, b) {
      const pa = (lookupLabel(a.label_key) || {}).priority || 999;
      const pb = (lookupLabel(b.label_key) || {}).priority || 999;
      return pa - pb;
    });
    // Zweite Zeile nur, wenn die schnellste Stufe NICHT schon «mehr als 10» zeigt
    // (Entscheid Tobias 2026-09-03): «mehr als 10 Stück sofort» plus «mehr als 10 Stück
    // in 7 bis 9 Tagen» sagt dem Kunden nichts. Der Warenkorb rechnet weiterhin über alle
    // Stufen (warehouse-cart-reconcile.js). Produktion auf Bestellung (mto) hat keine
    // Menge und bleibt ausgenommen. Gleiche Regel in hp-delivery-core.js renderCompact().
    const firstMeta = ordered.length ? lookupLabel(ordered[0].label_key) : null;
    const maxLines = (firstMeta && !firstMeta.mto && ordered[0].qty > QTY_DISPLAY_CAP)
      ? 1 : PDP_MAX_DELIVERY_LINES;
    return ordered.slice(0, maxLines).map(function (b) {
      const meta = lookupLabel(b.label_key);
      if (!meta) return '';
      // «Produktion auf Bestellung»: keine Stückzahl, Langform mit Monaten
      // («Produktion auf Bestellung (Lieferzeit ca. 3 bis 4 Monate)»).
      let lineText;
      if (meta.mto) {
        lineText = meta.label_standalone + window.HPDeliveryCore.mtoLongSuffix(b.months_min, b.months_max);
      } else {
        const unit = b.qty === 1 ? pw.one : pw.other;
        const qtyText = b.qty > QTY_DISPLAY_CAP
          ? (MORE_THAN_CAP[ACTIVE_LOCALE] || MORE_THAN_CAP.de)
          : b.qty;
        lineText = qtyText + ' ' + unit + ' ' + meta.label_lower;
      }
      return '<span class="hp-card__delivery-line" style="color:' + (meta.color || '') + '">' +
        '<span class="hp-avail__ic">' + window.HPDeliveryCore.checkSvg(meta.icon_color, 15) + '</span>' +
        '<span class="hp-avail__line-txt">' + lineText + '</span>' +
        '</span>';
    }).join('');
  }
  window.HP_DELIVERY = { LABEL_CATALOG: window.HPDeliveryCore.LABEL_CATALOG, renderBreakdownHTML: renderBreakdownHTML };

  // Kurzform-Regel (Entscheid Tobias 2026-07-29, LIEFERZEIT_KONZEPT.md): Die
  // Stufen-Zeile wird normal gerendert; bricht sie auf dem Gerät um, wird
  // «mehr als 10» durch «> 10» ersetzt und neu gerendert — dynamisch pro
  // Gerät/Sprache, keine festen Breiten-Schwellen. Nicht in jedem Extremfall
  // garantiert einzeilig (bewusst akzeptiert).
  const SHORT_MORE = '> 10';
  function applyShortTen(container) {
    const more = MORE_THAN_CAP[ACTIVE_LOCALE] || MORE_THAN_CAP.de;
    requestAnimationFrame(function () {
      container.querySelectorAll('.hp-avail__line-txt').forEach(function (span) {
        if (span.textContent.indexOf(more) === -1) return;
        const cs = getComputedStyle(span);
        let lh = parseFloat(cs.lineHeight);
        if (!lh || isNaN(lh)) lh = parseFloat(cs.fontSize) * 1.5;
        if (span.offsetHeight > lh * 1.5) {
          span.textContent = span.textContent.replace(more, SHORT_MORE);
        }
      });
    });
  }

  // --- PDP wiring -----------------------------------------------------------
  function initRoot(root) {
    if (root.dataset.hpDeliveryInit === '1') return;
    root.dataset.hpDeliveryInit = '1';
    const container = root.querySelector('[data-hp-delivery]');
    const dataEl = root.querySelector('[data-hp-delivery-data]');
    if (!container || !dataEl) return;
    let variantsData = {};
    try { variantsData = JSON.parse(dataEl.textContent); } catch (e) { return; }
    // Shopify-Verfügbarkeit pro Variante (aus Liquid, live nachgeführt über
    // on:variant:change). Härtung 2026-07-30 (Rehband-Vorfall): kaufbare
    // Variante darf NIE den Ausverkauft-Text tragen.
    let availMap = {};
    try { availMap = JSON.parse(root.getAttribute('data-hp-avail') || '{}'); } catch (e) { availMap = {}; }

    // Gehört ein <variant-picker> zu einem ANDEREN Produkt als diese Box?
    // (1) Picker im Auswahlfenster (quick-add-drawer), (2) Picker mit anderem
    // Produkt-Handle in data-url (z.B. Featured-Product-Sektion). Bewusst KEIN
    // Sektions-Vergleich: hp-mhd.js sitzt in einer anderen Sektion als der Picker.
    const ownScope = root.closest('quick-add-drawer, .js-quickbuy, .shopify-section');
    const ownHandle = root.getAttribute('data-hp-handle') || '';
    function isForeignPicker(src) {
      if (!src || !src.closest) return false;
      const drawer = src.closest('quick-add-drawer, .js-quickbuy');
      if (drawer) return !drawer.contains(root);
      const m = /\/products\/([^/?#]+)/.exec(src.getAttribute('data-url') || '');
      return !!(m && ownHandle && decodeURIComponent(m[1]) !== ownHandle);
    }

    function currentVariantId() {
      // update_url:true → the picker sets ?variant on change (most reliable).
      const u = new URLSearchParams(location.search).get('variant');
      if (u) return u;
      // Zuerst im eigenen Kontext suchen: Formulare fremder Produkte (Karten mit
      // Direkt-Add, Auswahlfenster) dürfen die Box nie steuern (Bug 2026-09-20).
      const ID_SEL = 'form[action*="/cart/add"] [name="id"], variant-picker [name="id"]';
      const idInput = (ownScope && ownScope.querySelector(ID_SEL)) || document.querySelector(ID_SEL);
      if (idInput && idInput.value) return idInput.value;
      return container.getAttribute('data-hp-delivery-current') || null;
    }
    // HIG-142 redesign: Area 2 (delivery ETA) + Area 3 (shipping cost), both in
    // the same .hp-avail box. Lieferzeit-Redesign 2026-07 (LIEFERZEIT_KONZEPT.md):
    // Die Kalenderzeile zeigt das konkrete Zustellfenster des SCHNELLSTEN
    // verfügbaren Stücks (instant → wie bisher datiert mit 17-Uhr-Cutoff);
    // versteckt bei OOS und bei «Produktion auf Bestellung» (dort ersetzt die
    // Statuszeile das WANN).
    const box = root.closest('.hp-avail') || document;
    const etaEl = box.querySelector('[data-hp-eta]');
    const etaText = box.querySelector('[data-hp-eta-text]');
    const shipChip = box.querySelector('[data-hp-ship-text]');
    // Auslaufend: Liquid-Flag (Produkt-Tag «auslaufend», sofort wirksam) ODER
    // das vom HPB-Sync geschriebene variants_data._disc (für Cards nötig).
    const discontinued = root.getAttribute('data-hp-discontinued') === '1' || variantsData._disc === 1;
    function fastestBucket(vd) {
      if (!vd || !vd.stock_breakdown || !vd.stock_breakdown.length) return null;
      let best = null, bestP = Infinity;
      vd.stock_breakdown.forEach(function (b) {
        const p = window.HPDeliveryCore.priorityOf(b);
        if (p < bestP) { bestP = p; best = b; }
      });
      return best;
    }
    function updateEta(vd) {
      if (!etaEl || !etaText) return;
      const fastest = fastestBucket(vd);
      if (!fastest) { etaEl.hidden = true; return; } // OOS / no data → hide
      const meta = window.HPDeliveryCore.lookupLabel(fastest.label_key);
      if (meta && meta.mto) { etaEl.hidden = true; return; } // Produktion: keine Kalenderzeile
      const txt = window.HPDeliveryCore.etaWindowText(fastest.label_key);
      if (!txt) { etaEl.hidden = true; return; } // z.B. over_12_weeks
      etaText.textContent = txt;
      etaEl.hidden = false;
    }
    // Ausverkauft-Zustände: Box ohne Portofrei-Zeile und Trennstrich (Entscheid
    // Tobias 2026-07-29/30). Bei Auslaufend zusätzlich den Benachrichtigen-Knopf
    // (STOQ) verstecken — es kommt nichts mehr, worüber man informieren könnte.
    function updateBoxState(vd) {
      const isOOS = !vd || !vd.stock_breakdown || !vd.stock_breakdown.length;
      box.classList.toggle('hp-avail--oos-final', isOOS && (discontinued || false));
      box.classList.toggle('hp-avail--oos', isOOS);
      if (discontinued) toggleNotify(isOOS);
    }
    let notifyTimers = [];
    function toggleNotify(hide) {
      notifyTimers.forEach(clearTimeout); notifyTimers = [];
      const apply = function () {
        const rx = /(Benachrichtige mich|Notify me when|Prévenez-moi|Avvisami quando)/i;
        document.querySelectorAll('button, a').forEach(function (el) {
          if (!rx.test(el.textContent || '')) return;
          const wrap = el.closest('div') || el;
          wrap.style.display = hide ? 'none' : '';
        });
      };
      apply();
      // STOQ injiziert seinen Knopf verzögert → zweimal nachfassen.
      if (hide) { notifyTimers.push(setTimeout(apply, 1000), setTimeout(apply, 3000)); }
    }
    // Kontakt-Zeile bei «Produktion auf Bestellung» — gleiche Optik wie die
    // Portofrei-Zeile, mit Sprechblasen-Icon; Link zur Kontaktseite (lokalisiert).
    function updateContact(vd) {
      const fastest = fastestBucket(vd);
      const meta = fastest ? window.HPDeliveryCore.lookupLabel(fastest.label_key) : null;
      const wantMto = !!(meta && meta.mto);
      let row = box.querySelector('[data-hp-contact]');
      if (!row && wantMto) {
        const shipRow = shipChip ? shipChip.parentElement : null;
        if (!shipRow || !shipRow.parentElement) return;
        row = document.createElement('div');
        row.className = 'hp-avail__ship hp-avail__contact';
        row.setAttribute('data-hp-contact', '');
        const S = CONTACT_STR[ACTIVE_LOCALE] || CONTACT_STR.de;
        const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        row.innerHTML = '<span class="hp-avail__ic"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M8 10.5h8M8 14h4m-6.5 5.5L3 21l1.2-3.6A8.96 8.96 0 0 1 3 12a9 9 0 1 1 9 9 8.96 8.96 0 0 1-4.5-1.2z"/></svg></span>' +
          '<span>' + S.pre + ' <a class="hp-avail__contact-link" href="' + rootPath + 'pages/kontakt">' + S.link + '</a></span>';
        const link = row.querySelector('a');
        if (link) link.addEventListener('click', function (e) { e.stopPropagation(); });
        shipRow.parentElement.appendChild(row);
      }
      if (row) row.hidden = !wantMto;
    }
    function updateShip(priceCents) {
      if (!shipChip || priceCents == null) return;
      const free = priceCents >= 15000;
      shipChip.classList.toggle('is-free', free);
      const txt = free ? shipChip.getAttribute('data-free') : shipChip.getAttribute('data-from');
      if (!txt) return;
      // Free shipping: colour only the leading BENEFIT word green ("Kostenloser" / "Free"),
      // rest neutral. Otherwise plain text. Built via DOM (no innerHTML of attribute text).
      const sp = free ? txt.indexOf(' ') : -1;
      if (sp > 0) {
        shipChip.textContent = '';
        const hl = document.createElement('span');
        hl.className = 'hp-ship-hl';
        hl.textContent = txt.slice(0, sp);
        shipChip.appendChild(hl);
        shipChip.appendChild(document.createTextNode(txt.slice(sp)));
      } else {
        shipChip.textContent = txt;
      }
    }
    const oosChecked = {};
    function verifyOosLive(vid) {
      if (oosChecked[vid]) return;
      oosChecked[vid] = true;
      const handle = root.getAttribute('data-hp-handle');
      if (!handle) return;
      const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(rootPath + 'products/' + handle + '.js', { cache: 'no-store' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p || !p.variants) return;
          const v = p.variants.find(function (x) { return String(x.id) === vid; });
          if (v && v.available) {
            availMap[vid] = 1;
            renderFor(currentVariantId());
          }
        })
        .catch(function () {});
    }
    function renderFor(vid) {
      const vd = vid != null ? variantsData[String(vid)] : null;
      const isAvail = vid != null ? availMap[String(vid)] : undefined;
      const breakdownEmpty = !vd || !vd.stock_breakdown || !vd.stock_breakdown.length;
      // Härtung (Rehband-Vorfall 2026-07-30): (a) FEHLENDER Metafeld-Eintrag ist
      // «keine Daten», NICHT «ausverkauft». (b) Ist die Variante laut Shopify
      // KAUFBAR, darf nie der Ausverkauft-Text erscheinen (stale Daten im
      // Übergangsfenster) — dann lieber gar keine Lieferzeile als eine falsche.
      if (breakdownEmpty && (!vd || isAvail === 1 || isAvail === true)) {
        container.innerHTML = '';
        if (etaEl) etaEl.hidden = true;
        box.classList.remove('hp-avail--oos', 'hp-avail--oos-final');
        const c = box.querySelector('[data-hp-contact]');
        if (c) c.hidden = true;
        return;
      }
      // Cache-Härtung (Rehband-Forensik 2026-07-30): Auch die Avail-Map stammt
      // aus demselben (möglicherweise CDN-gecachten) HTML. Bevor ein
      // Ausverkauft-Text stehen bleibt, einmalig FRISCH bei Shopify nachfragen;
      // ist die Variante in Wahrheit kaufbar, Zeile entfernen statt lügen.
      // Nur im seltenen OOS-Renderfall → keine Lastfrage.
      if (breakdownEmpty && vid != null) verifyOosLive(String(vid));
      container.innerHTML = renderBreakdownHTML(vd ? vd.stock_breakdown : [], { discontinued: discontinued });
      applyShortTen(container);
      updateEta(vd);
      updateBoxState(vd);
      updateContact(vd);
    }
    renderFor(currentVariantId());
    // Kurzform-Regel neu anwenden, wenn sich die Breite ändert (Rotation etc.).
    let rsT = null;
    window.addEventListener('resize', function () {
      clearTimeout(rsT);
      rsT = setTimeout(function () { renderFor(currentVariantId()); }, 250);
    });

    // The theme's <variant-picker> fires this (bubbling) AFTER resolving the
    // variant on every change. detail.variant is the full variant object, or
    // null for an unavailable/incomplete combination (→ OOS line). See
    // assets/variant-picker.js announceChange().
    // Fremd-Events ausfiltern (Bug 2026-09-20): Auch der Picker im Auswahlfenster
    // (quick-add-drawer, liegt ausserhalb jeder Sektion) und Picker anderer
    // Produkt-Sektionen feuern dieses Event bubbling bis zum document. Ohne Filter
    // wurde die Box des Hauptprodukts mit der FREMDEN Varianten-ID gerendert
    // (→ «keine Daten» → Lieferzeile leer, Portofrei-Zeile nach fremdem Preis).
    document.addEventListener('on:variant:change', function (e) {
      if (isForeignPicker(e.target)) return;
      const v = e.detail && e.detail.variant;
      if (v && v.id != null && typeof v.available !== 'undefined') {
        availMap[String(v.id)] = v.available ? 1 : 0;
      }
      renderFor(v ? v.id : null);
      if (v && typeof v.price === 'number') updateShip(v.price);
    });
    window.addEventListener('popstate', function () { renderFor(currentVariantId()); });
  }

  function initAll() {
    document.querySelectorAll('[data-hp-delivery-root]').forEach(initRoot);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
